// ============================================================================
// EXPANDED FACET SCHEMA - Full Ontology Coverage (73 Facet Types)
// ============================================================================

export type FacetCategory =
  | 'market_problems'       // Customer problems & pains
  | 'market_segments'       // Personas, segments, stakeholders
  | 'market_competition'    // Competitors, alternatives, trends
  | 'business_strategy'     // Goals, decisions, opportunities
  | 'business_model'        // Value prop, channels, revenue
  | 'business_risks'        // Risks, threats, weaknesses
  | 'solution_features'     // Features, requirements, stories
  | 'solution_constraints'  // Constraints, dependencies
  | 'solution_execution'    // Actions, milestones, metrics
  | 'insights_only'         // No entity created (contextual)

export interface FacetTypeDefinition {
  id: string
  name: string
  description: string
  category: FacetCategory
  icon: string
  color: string
  defaultEntityType: string | null  // Maps to ontology entity, null = insight-only
  detectionHints: string[]          // Help AI detect this
  isEnabled: boolean                // Toggle on/off
}

export const FACET_CATEGORY_LABELS: Record<FacetCategory, string> = {
  market_problems: 'Market Problems',
  market_segments: 'Market Segments',
  market_competition: 'Competition & Trends',
  business_strategy: 'Business Strategy',
  business_model: 'Business Model',
  business_risks: 'Risks & Weaknesses',
  solution_features: 'Solution Features',
  solution_constraints: 'Constraints',
  solution_execution: 'Execution',
  insights_only: 'Insights Only',
}

export const FACET_CATEGORY_ORDER: FacetCategory[] = [
  'market_problems',
  'market_segments',
  'market_competition',
  'business_strategy',
  'business_model',
  'business_risks',
  'solution_features',
  'solution_constraints',
  'solution_execution',
  'insights_only',
]

// ============================================================================
// FULL EXPANDED FACET DEFINITIONS (73 types)
// ============================================================================

export const EXPANDED_FACET_SCHEMA: FacetTypeDefinition[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // MARKET DOMAIN - Problems & Pains
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'problem',
    name: 'Problem',
    description: 'A pain point, challenge, or difficulty users experience',
    category: 'market_problems',
    icon: '!',
    color: '#dc3545',
    defaultEntityType: 'Problem',
    detectionHints: ['struggle with', 'difficult to', 'pain point', 'challenge', 'frustrating'],
    isEnabled: true,
  },
  {
    id: 'customer_pain',
    name: 'Customer Pain',
    description: 'Negative emotions, costs, or risks customers want to avoid',
    category: 'market_problems',
    icon: '💔',
    color: '#dc3545',
    defaultEntityType: 'CustomerPain',
    detectionHints: ['annoyed by', 'costs too much', 'waste time', 'risky', 'afraid of'],
    isEnabled: true,
  },
  {
    id: 'customer_gain',
    name: 'Customer Gain',
    description: 'Outcomes and benefits customers want to achieve',
    category: 'market_problems',
    icon: '✨',
    color: '#28a745',
    defaultEntityType: 'CustomerGain',
    detectionHints: ['would love', 'want to achieve', 'benefit from', 'ideal outcome', 'dream of'],
    isEnabled: true,
  },
  {
    id: 'customer_job',
    name: 'Customer Job',
    description: 'Tasks customers are trying to accomplish',
    category: 'market_problems',
    icon: '📋',
    color: '#6f42c1',
    defaultEntityType: 'CustomerJob',
    detectionHints: ['trying to', 'need to accomplish', 'task', 'goal', 'objective'],
    isEnabled: true,
  },
  {
    id: 'workaround',
    name: 'Workaround',
    description: 'Current solutions or hacks users employ',
    category: 'market_problems',
    icon: '🔧',
    color: '#fd7e14',
    defaultEntityType: 'Workaround',
    detectionHints: ['currently use', 'work around', 'hack', 'manual process', 'alternative way'],
    isEnabled: true,
  },
  {
    id: 'current_behavior',
    name: 'Current Behavior',
    description: 'How users currently handle a situation',
    category: 'market_problems',
    icon: '🔄',
    color: '#17a2b8',
    defaultEntityType: 'Problem',  // Maps to Problem (behavioral insight)
    detectionHints: ['currently', 'right now', 'today we', 'existing process'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // MARKET DOMAIN - Segments & Stakeholders
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'persona',
    name: 'Persona',
    description: 'A user archetype or customer profile',
    category: 'market_segments',
    icon: '👤',
    color: '#4a90a4',
    defaultEntityType: 'Persona',
    detectionHints: ['type of user', 'user profile', 'customer type', 'target audience'],
    isEnabled: true,
  },
  {
    id: 'customer_segment',
    name: 'Customer Segment',
    description: 'A group of customers with shared characteristics',
    category: 'market_segments',
    icon: '👥',
    color: '#4a90a4',
    defaultEntityType: 'CustomerSegment',
    detectionHints: ['segment', 'market segment', 'customer group', 'target market'],
    isEnabled: true,
  },
  {
    id: 'stakeholder',
    name: 'Stakeholder',
    description: 'Person or group with interest in the outcome',
    category: 'market_segments',
    icon: '@',
    color: '#4a90a4',
    defaultEntityType: 'Stakeholder',
    detectionHints: ['stakeholder', 'decision maker', 'influencer', 'approver', 'sponsor'],
    isEnabled: true,
  },
  {
    id: 'person',
    name: 'Person',
    description: 'A specific individual mentioned',
    category: 'market_segments',
    icon: '👤',
    color: '#4a90a4',
    defaultEntityType: 'Person',
    detectionHints: ['name mentioned', 'interviewee', 'customer name', 'contact'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // MARKET DOMAIN - Competition & Trends
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'competitor',
    name: 'Competitor',
    description: 'A competing product, service, or company',
    category: 'market_competition',
    icon: '⚔️',
    color: '#e83e8c',
    defaultEntityType: 'Competitor',
    detectionHints: ['competitor', 'alternative', 'compared to', 'switched from', 'using instead'],
    isEnabled: true,
  },
  {
    id: 'alternative',
    name: 'Alternative',
    description: 'An existing solution users consider',
    category: 'market_competition',
    icon: '↔️',
    color: '#e83e8c',
    defaultEntityType: 'Alternative',
    detectionHints: ['alternative', 'other option', 'could also use', 'substitute'],
    isEnabled: true,
  },
  {
    id: 'trend',
    name: 'Market Trend',
    description: 'An industry or market trend',
    category: 'market_competition',
    icon: '📈',
    color: '#20c997',
    defaultEntityType: 'Trend',
    detectionHints: ['trend', 'growing', 'shifting towards', 'increasingly', 'emerging'],
    isEnabled: true,
  },
  {
    id: 'industry',
    name: 'Industry',
    description: 'Industry or market sector reference',
    category: 'market_competition',
    icon: '🏭',
    color: '#6c757d',
    defaultEntityType: 'Industry',
    detectionHints: ['industry', 'sector', 'market', 'vertical', 'B2B', 'B2C', 'enterprise'],
    isEnabled: true,
  },
  {
    id: 'market_signal',
    name: 'Market Signal',
    description: 'Weak or strong signal from the market',
    category: 'market_competition',
    icon: '📡',
    color: '#17a2b8',
    defaultEntityType: 'MarketSignal',
    detectionHints: ['signal', 'indicator', 'early sign', 'emerging pattern', 'weak signal'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // BUSINESS DOMAIN - Strategy
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'strategic_goal',
    name: 'Strategic Goal',
    description: 'High-level business objective',
    category: 'business_strategy',
    icon: '🎯',
    color: '#5c8a6b',
    defaultEntityType: 'StrategicGoal',
    detectionHints: ['goal', 'objective', 'target', 'aim to', 'vision'],
    isEnabled: true,
  },
  {
    id: 'decision',
    name: 'Decision',
    description: 'A choice that was made or needs to be made',
    category: 'business_strategy',
    icon: '↗',
    color: '#5c8a6b',
    defaultEntityType: 'Decision',
    detectionHints: ['decided to', 'choice', 'chose', 'agreed to', 'resolution'],
    isEnabled: true,
  },
  {
    id: 'rationale',
    name: 'Rationale',
    description: 'Reasoning behind a decision',
    category: 'business_strategy',
    icon: '💡',
    color: '#5c8a6b',
    defaultEntityType: 'Decision',  // Links to Decision entity
    detectionHints: ['because', 'reason', 'rationale', 'justification', 'why we'],
    isEnabled: true,
  },
  {
    id: 'opportunity',
    name: 'Opportunity',
    description: 'A potential win or opening',
    category: 'business_strategy',
    icon: '★',
    color: '#28a745',
    defaultEntityType: 'Opportunity',
    detectionHints: ['opportunity', 'potential', 'could benefit', 'upside', 'chance to'],
    isEnabled: true,
  },
  {
    id: 'value_proposition',
    name: 'Value Proposition',
    description: 'The value offered to customers',
    category: 'business_strategy',
    icon: '💎',
    color: '#6f42c1',
    defaultEntityType: 'ValueProposition',
    detectionHints: ['value proposition', 'unique value', 'benefit', 'why choose us'],
    isEnabled: true,
  },
  {
    id: 'strategy',
    name: 'Strategy',
    description: 'Strategic direction or approach',
    category: 'business_strategy',
    icon: '🧭',
    color: '#5c8a6b',
    defaultEntityType: 'Strategy',
    detectionHints: ['strategy', 'strategic', 'approach', 'direction', 'roadmap', 'plan'],
    isEnabled: true,
  },
  {
    id: 'business_context',
    name: 'Business Context',
    description: 'Background business context or constraint',
    category: 'business_strategy',
    icon: '📋',
    color: '#6c757d',
    defaultEntityType: 'BusinessContext',
    detectionHints: ['context', 'background', 'given that', 'considering', 'in light of'],
    isEnabled: true,
  },
  {
    id: 'gtm_strategy',
    name: 'Go-to-Market Strategy',
    description: 'Go-to-market approach or launch strategy',
    category: 'business_strategy',
    icon: '🚀',
    color: '#e83e8c',
    defaultEntityType: 'GoToMarketStrategy',
    detectionHints: ['go-to-market', 'GTM', 'launch', 'market entry', 'rollout', 'release strategy'],
    isEnabled: true,
  },
  {
    id: 'positioning',
    name: 'Positioning',
    description: 'Market positioning or differentiation',
    category: 'business_strategy',
    icon: '🎯',
    color: '#e83e8c',
    defaultEntityType: 'Positioning',
    detectionHints: ['positioning', 'differentiate', 'unique', 'stand out', 'compared to', 'unlike'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // BUSINESS DOMAIN - Business Model Canvas
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'channel',
    name: 'Channel',
    description: 'Touchpoint for reaching customers or delivering value',
    category: 'business_model',
    icon: '📢',
    color: '#17a2b8',
    defaultEntityType: 'Channel',
    detectionHints: ['channel', 'reach customers', 'sales channel', 'distribution', 'touchpoint', 'marketing channel'],
    isEnabled: true,
  },
  {
    id: 'customer_relationship',
    name: 'Customer Relationship',
    description: 'Type of relationship with customer segments',
    category: 'business_model',
    icon: '🤝',
    color: '#17a2b8',
    defaultEntityType: 'CustomerRelationship',
    detectionHints: ['relationship', 'customer support', 'self-service', 'community', 'personal assistance', 'retention'],
    isEnabled: true,
  },
  {
    id: 'revenue_stream',
    name: 'Revenue Stream',
    description: 'How the business earns money',
    category: 'business_model',
    icon: '💰',
    color: '#28a745',
    defaultEntityType: 'RevenueStream',
    detectionHints: ['revenue', 'pricing', 'monetization', 'subscription', 'licensing', 'fee', 'payment'],
    isEnabled: true,
  },
  {
    id: 'key_resource',
    name: 'Key Resource',
    description: 'Critical assets needed to deliver value',
    category: 'business_model',
    icon: '🏗️',
    color: '#fd7e14',
    defaultEntityType: 'KeyResource',
    detectionHints: ['resource', 'asset', 'infrastructure', 'platform', 'intellectual property', 'team', 'technology'],
    isEnabled: true,
  },
  {
    id: 'key_activity',
    name: 'Key Activity',
    description: 'Critical activities to deliver value proposition',
    category: 'business_model',
    icon: '⚙️',
    color: '#fd7e14',
    defaultEntityType: 'KeyActivity',
    detectionHints: ['activity', 'operation', 'process', 'production', 'development', 'platform management'],
    isEnabled: true,
  },
  {
    id: 'key_partnership',
    name: 'Key Partnership',
    description: 'Strategic partners and suppliers',
    category: 'business_model',
    icon: '🤝',
    color: '#6f42c1',
    defaultEntityType: 'KeyPartnership',
    detectionHints: ['partner', 'partnership', 'supplier', 'alliance', 'vendor', 'integration partner'],
    isEnabled: true,
  },
  {
    id: 'cost_structure',
    name: 'Cost Structure',
    description: 'Costs incurred to operate the business model',
    category: 'business_model',
    icon: '💸',
    color: '#dc3545',
    defaultEntityType: 'CostStructure',
    detectionHints: ['cost', 'expense', 'investment', 'budget', 'fixed cost', 'variable cost', 'overhead'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // BUSINESS DOMAIN - Risks & Weaknesses
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'risk',
    name: 'Risk',
    description: 'A potential threat or blocker',
    category: 'business_risks',
    icon: '⚠',
    color: '#dc3545',
    defaultEntityType: 'Risk',
    detectionHints: ['risk', 'might fail', 'danger', 'concern', 'could go wrong'],
    isEnabled: true,
  },
  {
    id: 'threat',
    name: 'Threat',
    description: 'An external threat to the business',
    category: 'business_risks',
    icon: '⛔',
    color: '#dc3545',
    defaultEntityType: 'Threat',
    detectionHints: ['threat', 'external risk', 'market threat', 'competitive threat'],
    isEnabled: true,
  },
  {
    id: 'weakness',
    name: 'Weakness',
    description: 'An internal weakness or gap',
    category: 'business_risks',
    icon: '📉',
    color: '#fd7e14',
    defaultEntityType: 'Weakness',
    detectionHints: ['weakness', 'gap', 'lack', 'missing capability', 'need to improve'],
    isEnabled: true,
  },
  {
    id: 'strength',
    name: 'Strength',
    description: 'An internal strength or advantage',
    category: 'business_risks',
    icon: '💪',
    color: '#28a745',
    defaultEntityType: 'Strength',
    detectionHints: ['strength', 'advantage', 'good at', 'capability', 'competency'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // SOLUTION DOMAIN - Features & Requirements
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'feature_request',
    name: 'Feature Request',
    description: 'A requested product feature',
    category: 'solution_features',
    icon: '✨',
    color: '#6f42c1',
    defaultEntityType: 'Feature',
    detectionHints: ['feature', 'would be nice', 'request', 'want to be able to', 'wishlist'],
    isEnabled: true,
  },
  {
    id: 'requirement',
    name: 'Requirement',
    description: 'A must-have requirement',
    category: 'solution_features',
    icon: '📌',
    color: '#6f42c1',
    defaultEntityType: 'Requirement',
    detectionHints: ['requirement', 'must have', 'need', 'required', 'mandatory'],
    isEnabled: true,
  },
  {
    id: 'user_story',
    name: 'User Story',
    description: 'A user story or use case',
    category: 'solution_features',
    icon: '📖',
    color: '#6f42c1',
    defaultEntityType: 'UserStory',
    detectionHints: ['as a user', 'I want to', 'so that', 'use case', 'scenario'],
    isEnabled: true,
  },
  {
    id: 'product',
    name: 'Product',
    description: 'A product or service mention',
    category: 'solution_features',
    icon: '📦',
    color: '#6f42c1',
    defaultEntityType: 'Product',
    detectionHints: ['product', 'service', 'platform', 'application', 'solution', 'tool'],
    isEnabled: true,
  },
  {
    id: 'theme',
    name: 'Theme',
    description: 'A strategic theme or initiative',
    category: 'solution_features',
    icon: '🎨',
    color: '#17a2b8',
    defaultEntityType: 'Theme',
    detectionHints: ['theme', 'initiative', 'program', 'strategic area', 'pillar'],
    isEnabled: true,
  },
  {
    id: 'epic',
    name: 'Epic',
    description: 'A large body of work or initiative',
    category: 'solution_features',
    icon: '📚',
    color: '#17a2b8',
    defaultEntityType: 'Epic',
    detectionHints: ['epic', 'initiative', 'large feature', 'capability', 'module'],
    isEnabled: true,
  },
  {
    id: 'pattern',
    name: 'Pattern',
    description: 'A design or architecture pattern',
    category: 'solution_features',
    icon: '🔷',
    color: '#6c757d',
    defaultEntityType: 'Pattern',
    detectionHints: ['pattern', 'architecture', 'design', 'approach', 'best practice'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // SOLUTION DOMAIN - Constraints
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'constraint',
    name: 'Constraint',
    description: 'A limitation or requirement',
    category: 'solution_constraints',
    icon: '⊘',
    color: '#8b5cf6',
    defaultEntityType: 'Constraint',
    detectionHints: ['constraint', 'limitation', 'cannot', 'must not', 'restricted'],
    isEnabled: true,
  },
  {
    id: 'dependency',
    name: 'Dependency',
    description: 'A dependency on another system or team',
    category: 'solution_constraints',
    icon: '🔗',
    color: '#8b5cf6',
    defaultEntityType: 'Dependency',
    detectionHints: ['depends on', 'dependency', 'blocked by', 'waiting for', 'requires'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // SOLUTION DOMAIN - Execution
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'action_item',
    name: 'Action Item',
    description: 'A task or next step',
    category: 'solution_execution',
    icon: '☐',
    color: '#d4a020',
    defaultEntityType: 'ActionItem',
    detectionHints: ['action item', 'todo', 'next step', 'follow up', 'task'],
    isEnabled: true,
  },
  {
    id: 'milestone',
    name: 'Milestone',
    description: 'A project milestone or deadline',
    category: 'solution_execution',
    icon: '🏁',
    color: '#d4a020',
    defaultEntityType: 'Milestone',
    detectionHints: ['milestone', 'deadline', 'target date', 'by when', 'launch'],
    isEnabled: true,
  },
  {
    id: 'success_metric',
    name: 'Success Metric',
    description: 'A measure of success or KPI',
    category: 'solution_execution',
    icon: '📊',
    color: '#28a745',
    defaultEntityType: 'SuccessMetric',
    detectionHints: ['metric', 'KPI', 'measure', 'success criteria', 'target'],
    isEnabled: true,
  },
  {
    id: 'metric',
    name: 'Metric',
    description: 'Key performance indicator or measurement',
    category: 'solution_execution',
    icon: '📈',
    color: '#28a745',
    defaultEntityType: 'Metric',
    detectionHints: ['KPI', 'metric', 'measure', 'track', 'monitor', 'performance indicator', 'success metric'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // P1 - HIGH VALUE MARKET & BUSINESS FACETS
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'market_analysis',
    name: 'Market Analysis',
    description: 'Discussion of market size, TAM/SAM/SOM, market dynamics',
    category: 'market_competition',
    icon: '📊',
    color: '#5c8a6b',
    defaultEntityType: 'Market',
    detectionHints: ['total addressable market', 'TAM', 'SAM', 'serviceable market', 'market size', 'market is worth', 'billion dollar market', 'market growth'],
    isEnabled: true,
  },
  {
    id: 'market_opportunity',
    name: 'Market Opportunity',
    description: 'Identified opportunity or gap in the market',
    category: 'business_strategy',
    icon: '🎯',
    color: '#28a745',
    defaultEntityType: 'MarketOpportunity',
    detectionHints: ['market gap', 'untapped market', 'whitespace', 'opportunity to', 'unmet need', 'underserved market', 'market opening'],
    isEnabled: true,
  },
  {
    id: 'customer_journey',
    name: 'Customer Journey',
    description: 'End-to-end customer experience path',
    category: 'market_segments',
    icon: '🗺️',
    color: '#6f42c1',
    defaultEntityType: 'CustomerJourney',
    detectionHints: ['customer journey', 'user journey', 'from awareness to purchase', 'buying process', 'customer lifecycle', 'end-to-end experience'],
    isEnabled: true,
  },
  {
    id: 'journey_stage',
    name: 'Journey Stage',
    description: 'Specific stage in customer journey (awareness, consideration, purchase, etc.)',
    category: 'market_segments',
    icon: '📍',
    color: '#6f42c1',
    defaultEntityType: 'JourneyStage',
    detectionHints: ['awareness stage', 'consideration phase', 'decision stage', 'purchase moment', 'onboarding phase', 'retention stage', 'post-purchase'],
    isEnabled: true,
  },
  {
    id: 'touchpoint',
    name: 'Touchpoint',
    description: 'Point of interaction between customer and product/company',
    category: 'market_segments',
    icon: '👆',
    color: '#17a2b8',
    defaultEntityType: 'Touchpoint',
    detectionHints: ['touchpoint', 'interaction point', 'customer interaction', 'engagement point', 'contact point', 'when they interact with'],
    isEnabled: true,
  },
  {
    id: 'customer_need',
    name: 'Customer Need',
    description: 'Fundamental need driving customer behavior (deeper than pain/gain)',
    category: 'market_problems',
    icon: '💡',
    color: '#ffc107',
    defaultEntityType: 'CustomerNeed',
    detectionHints: ['customers need', 'fundamental need', 'core requirement', 'essential for', 'must have', 'basic need'],
    isEnabled: true,
  },
  {
    id: 'regulation',
    name: 'Regulation',
    description: 'Regulatory requirement or compliance obligation',
    category: 'solution_constraints',
    icon: '⚖️',
    color: '#dc3545',
    defaultEntityType: 'Regulation',
    detectionHints: ['GDPR', 'compliance', 'regulation', 'regulatory requirement', 'must comply with', 'legal requirement', 'mandated by'],
    isEnabled: true,
  },
  {
    id: 'demographic',
    name: 'Demographic',
    description: 'Demographic characteristic (age, location, industry, company size)',
    category: 'market_segments',
    icon: '👥',
    color: '#4a90a4',
    defaultEntityType: 'Demographic',
    detectionHints: ['age group', 'located in', 'industry', 'company size', 'employees', 'revenue range', 'geographic'],
    isEnabled: true,
  },
  {
    id: 'psychographic',
    name: 'Psychographic',
    description: 'Psychological characteristic (values, attitudes, lifestyle)',
    category: 'market_segments',
    icon: '🧠',
    color: '#6f42c1',
    defaultEntityType: 'Psychographic',
    detectionHints: ['value', 'belief', 'attitude', 'lifestyle', 'personality', 'risk-averse', 'early adopter'],
    isEnabled: true,
  },
  {
    id: 'assumption',
    name: 'Assumption',
    description: 'Hypothesis or assumption that needs validation',
    category: 'business_strategy',
    icon: '🤔',
    color: '#fd7e14',
    defaultEntityType: 'Assumption',
    detectionHints: ['we assume', 'hypothesis', 'belief that', 'if we believe', 'assuming that', 'based on assumption', 'untested belief'],
    isEnabled: true,
  },
  {
    id: 'experiment',
    name: 'Experiment',
    description: 'Test or experiment to validate assumption',
    category: 'business_strategy',
    icon: '🧪',
    color: '#17a2b8',
    defaultEntityType: 'Experiment',
    detectionHints: ['test', 'experiment', 'A/B test', 'pilot', 'trial', 'validate by', 'run experiment'],
    isEnabled: true,
  },
  {
    id: 'partner',
    name: 'Partner',
    description: 'Strategic partner or key partnership',
    category: 'business_model',
    icon: '🤝',
    color: '#6f42c1',
    defaultEntityType: 'Partner',
    detectionHints: ['partner with', 'strategic partnership', 'collaborate with', 'work with', 'alliance', 'joint venture'],
    isEnabled: true,
  },
  {
    id: 'pricing_model',
    name: 'Pricing Model',
    description: 'Pricing strategy or model',
    category: 'business_model',
    icon: '💰',
    color: '#28a745',
    defaultEntityType: 'PricingModel',
    detectionHints: ['pricing', 'subscription', 'freemium', 'tiered pricing', 'per user', 'usage-based', 'pricing strategy'],
    isEnabled: true,
  },
  {
    id: 'customer_acquisition',
    name: 'Customer Acquisition',
    description: 'Customer acquisition strategy or channel',
    category: 'business_model',
    icon: '🎣',
    color: '#fd7e14',
    defaultEntityType: 'CustomerAcquisition',
    detectionHints: ['acquire customers', 'CAC', 'customer acquisition', 'acquisition channel', 'get customers', 'lead generation'],
    isEnabled: true,
  },
  {
    id: 'retention_strategy',
    name: 'Retention Strategy',
    description: 'Strategy to retain customers and reduce churn',
    category: 'business_model',
    icon: '🔄',
    color: '#28a745',
    defaultEntityType: 'RetentionStrategy',
    detectionHints: ['retain customers', 'reduce churn', 'retention', 'keep customers', 'loyalty program', 'customer success'],
    isEnabled: true,
  },
  {
    id: 'release',
    name: 'Release',
    description: 'Product release or version',
    category: 'solution_execution',
    icon: '🚀',
    color: '#28a745',
    defaultEntityType: 'Release',
    detectionHints: ['release', 'version', 'launch', 'ship', 'deploy', 'go live', 'v1.0'],
    isEnabled: true,
  },
  {
    id: 'sprint',
    name: 'Sprint',
    description: 'Agile sprint or iteration',
    category: 'solution_execution',
    icon: '🏃',
    color: '#17a2b8',
    defaultEntityType: 'Sprint',
    detectionHints: ['sprint', 'iteration', 'two-week sprint', 'sprint goal', 'sprint planning', 'next sprint'],
    isEnabled: true,
  },
  {
    id: 'task',
    name: 'Task',
    description: 'Development task or work item',
    category: 'solution_execution',
    icon: '☑️',
    color: '#6c757d',
    defaultEntityType: 'Task',
    detectionHints: ['task', 'work item', 'ticket', 'JIRA', 'subtask', 'to-do'],
    isEnabled: true,
  },
  {
    id: 'acceptance_criteria',
    name: 'Acceptance Criteria',
    description: 'Conditions that must be met for story acceptance',
    category: 'solution_features',
    icon: '✅',
    color: '#28a745',
    defaultEntityType: 'AcceptanceCriteria',
    detectionHints: ['acceptance criteria', 'AC:', 'given when then', 'criteria', 'must', 'should', 'definition of done'],
    isEnabled: true,
  },
  {
    id: 'test_case',
    name: 'Test Case',
    description: 'Test scenario or test case',
    category: 'solution_features',
    icon: '🧪',
    color: '#17a2b8',
    defaultEntityType: 'TestCase',
    detectionHints: ['test case', 'test scenario', 'should test', 'verify that', 'edge case', 'happy path'],
    isEnabled: true,
  },
  {
    id: 'technical_debt',
    name: 'Technical Debt',
    description: 'Technical debt or code quality issue',
    category: 'solution_constraints',
    icon: '⚠️',
    color: '#dc3545',
    defaultEntityType: 'TechnicalDebt',
    detectionHints: ['technical debt', 'tech debt', 'refactor', 'legacy code', 'needs cleanup', 'code smell'],
    isEnabled: true,
  },
  {
    id: 'component',
    name: 'Component',
    description: 'System component or module',
    category: 'solution_features',
    icon: '🧩',
    color: '#6f42c1',
    defaultEntityType: 'Component',
    detectionHints: ['component', 'module', 'service', 'microservice', 'API', 'library'],
    isEnabled: true,
  },

  // ═══════════════════════════════════════════════════════════════════════
  // INSIGHTS ONLY (No Entity Created)
  // ═══════════════════════════════════════════════════════════════════════
  {
    id: 'quote',
    name: 'Notable Quote',
    description: 'A significant quote worth preserving',
    category: 'insights_only',
    icon: '"',
    color: '#6c757d',
    defaultEntityType: null,  // No entity
    detectionHints: ['quote', 'said', 'mentioned', 'verbatim'],
    isEnabled: true,
  },
  {
    id: 'context',
    name: 'Context',
    description: 'Background context for understanding',
    category: 'insights_only',
    icon: '📝',
    color: '#6c757d',
    defaultEntityType: null,
    detectionHints: ['context', 'background', 'for reference'],
    isEnabled: true,
  },
  {
    id: 'observation',
    name: 'Observation',
    description: 'A general observation or note',
    category: 'insights_only',
    icon: '👁',
    color: '#6c757d',
    defaultEntityType: null,
    detectionHints: ['observed', 'noticed', 'interesting'],
    isEnabled: true,
  },
]

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get facet type definition by ID
 */
export function getFacetById(id: string): FacetTypeDefinition | undefined {
  return EXPANDED_FACET_SCHEMA.find(f => f.id === id)
}

/**
 * Get all facets for a category
 */
export function getFacetsByCategory(category: FacetCategory): FacetTypeDefinition[] {
  return EXPANDED_FACET_SCHEMA.filter(f => f.category === category)
}

/**
 * Get enabled facets only
 */
export function getEnabledFacets(): FacetTypeDefinition[] {
  return EXPANDED_FACET_SCHEMA.filter(f => f.isEnabled)
}

/**
 * Get facets that create entities (not insight-only)
 */
export function getEntityFacets(): FacetTypeDefinition[] {
  return EXPANDED_FACET_SCHEMA.filter(f => f.defaultEntityType !== null)
}

/**
 * Get insight-only facets
 */
export function getInsightOnlyFacets(): FacetTypeDefinition[] {
  return EXPANDED_FACET_SCHEMA.filter(f => f.defaultEntityType === null)
}

/**
 * Map of facet ID to definition for quick lookup
 */
export const FACET_MAP = new Map<string, FacetTypeDefinition>(
  EXPANDED_FACET_SCHEMA.map(f => [f.id, f])
)

/**
 * Get all unique entity types that facets can create
 */
export function getTargetEntityTypes(): string[] {
  const types = new Set<string>()
  EXPANDED_FACET_SCHEMA.forEach(f => {
    if (f.defaultEntityType) {
      types.add(f.defaultEntityType)
    }
  })
  return Array.from(types).sort()
}
